import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

interface CandidateFilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  status: string;
  cheatingStatus: string;
  onApply: (status: string, cheatingStatus: string) => void;
  onReset: () => void;
}

const CandidateFilterModal: React.FC<CandidateFilterModalProps> = ({
  isOpen,
  onClose,
  status,
  cheatingStatus,
  onApply,
  onReset
}) => {
  const [draftStatus, setDraftStatus] = useState(status);
  const [draftCheatingStatus, setDraftCheatingStatus] = useState(cheatingStatus);

  // Sync state with props when modal opens or props change
  useEffect(() => {
    if (isOpen) {
      setDraftStatus(status);
      setDraftCheatingStatus(cheatingStatus);
    }
  }, [isOpen, status, cheatingStatus]);

  const handleApply = () => {
    onApply(draftStatus, draftCheatingStatus);
  };

  const handleReset = () => {
    setDraftStatus("");
    setDraftCheatingStatus("");
    onReset();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "12px",
          padding: "8px",
          backgroundColor: "var(--surface)",
          color: "var(--text)",
          border: "1px solid var(--border)",
          boxShadow: "var(--shadow-lg)"
        }
      }}
    >
      <DialogTitle sx={{ m: 0, p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", backgroundColor: "var(--surface)" }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 700, color: "var(--text)" }}>
          Filter Candidates
        </Typography>
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            color: "var(--text-3)",
            "&:hover": { color: "var(--text-2)" }
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderTop: "1px solid var(--border-soft)", borderBottom: "1px solid var(--border-soft)", backgroundColor: "var(--surface)" }}>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, my: 1 }}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="filter-status-label" sx={{ fontWeight: 500, color: "var(--text-2)", "&.Mui-focused": { color: "var(--accent)" } }}>Status</InputLabel>
            <Select
              labelId="filter-status-label"
              id="filter-status"
              value={draftStatus}
              onChange={(e) => setDraftStatus(e.target.value)}
              label="Status"
              sx={{
                borderRadius: "8px",
                color: "var(--text)",
                backgroundColor: "var(--surface-2)",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--border)" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--border-strong)" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--accent)" },
                "& .MuiSvgIcon-root": { color: "var(--text-2)" }
              }}
            >
              <MenuItem value="INVITED">Invited</MenuItem>
              <MenuItem value="IN_PROGRESS">In Progress</MenuItem>
              <MenuItem value="SUBMITTED">Submitted</MenuItem>
              <MenuItem value="TIME_EXPIRED">Time Expired</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth variant="outlined">
            <InputLabel id="filter-cheating-label" sx={{ fontWeight: 500, color: "var(--text-2)", "&.Mui-focused": { color: "var(--accent)" } }}>Cheating Status</InputLabel>
            <Select
              labelId="filter-cheating-label"
              id="filter-cheating"
              value={draftCheatingStatus}
              onChange={(e) => setDraftCheatingStatus(e.target.value)}
              label="Cheating Status"
              sx={{
                borderRadius: "8px",
                color: "var(--text)",
                backgroundColor: "var(--surface-2)",
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "var(--border)" },
                "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "var(--border-strong)" },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "var(--accent)" },
                "& .MuiSvgIcon-root": { color: "var(--text-2)" }
              }}
            >
              <MenuItem value="NOT_DETECTED">Not Detected</MenuItem>
              <MenuItem value="LIKELY">Likely</MenuItem>
              <MenuItem value="DETECTED">Detected</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2, justifyContent: "space-between", backgroundColor: "var(--surface)" }}>
        <Button
          onClick={handleReset}
          variant="text"
          sx={{
            color: "var(--text-2)",
            fontWeight: 600,
            textTransform: "none",
            "&:hover": { backgroundColor: "var(--surface-2)", color: "var(--text)" }
          }}
        >
          Reset All
        </Button>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            onClick={onClose}
            variant="outlined"
            sx={{
              borderRadius: "8px",
              borderColor: "var(--border)",
              color: "var(--text-2)",
              fontWeight: 600,
              textTransform: "none",
              "&:hover": { borderColor: "var(--border-strong)", backgroundColor: "var(--surface-2)", color: "var(--text)" }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            sx={{
              borderRadius: "8px",
              backgroundColor: "var(--accent)",
              color: "var(--text-on-accent)",
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "none",
              "&:hover": { backgroundColor: "var(--accent-strong)", boxShadow: "none" }
            }}
          >
            Apply Filters
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default CandidateFilterModal;
